import { action, observable, runInAction } from "mobx";
import { api, api_adm } from "../../api";
import { toast } from "react-toastify";

export class _MvaStorePA {
  obs = observable({
    mvas: [] as any[],
    currentPage: 1 as number,
    length_mvas: 0 as number,
    filter_vigencia: true as boolean,
    selectedTab: "entrada" as string
  })

  @action

  /**
  * Filtra e pagina os dados de MVAs com base nos critérios fornecidos.
  *
  * A função:
  * - Monta um objeto de requisição contendo:
  *   - `page`: número da página atual.
  *   - `limit`: quantidade de itens por página (fixo em 20).
  *   - `filter`: string de filtro com pontos (`.`) removidos.
  *   - `vigencia`: valor atual do filtro de vigência (`this.obs.filter_vigencia`).
  *   - `entrada`: booleano indicando se a aba selecionada é "entrada".
  * - Envia uma requisição POST para `/api/mvas/index` com os filtros definidos.
  * - Limpa os dados atuais de MVAs antes de preencher com os novos dados:
  *   - Zera `this.obs.mvas` e `this.obs.length_mvas`.
  *   - Caso a resposta (`resp.data.itens`) contenha resultados:
  *     - Atualiza `this.obs.mvas` com os itens retornados.
  *     - Atualiza `this.obs.length_mvas` com a contagem total (`resp.data.count`).
  * - Em caso de erro na requisição ou processamento, exibe um **toast.error** com a mensagem "Erro ao filtrar Mvas!".
  *
  * @async
  * @param {number} page Número da página solicitada.
  * @param {string} filter Texto utilizado para filtragem.
  *
  * @returns {Promise<void>} Promise resolvida após a tentativa de filtragem.
  */
  async handlePageFilter(page: number, filter: string) {
    try {
      let data = {
        page: page,
        limit: 20,
        filter: filter.replace(/\./g, ""),
        vigencia: this.obs.filter_vigencia,
        entrada: this.obs.selectedTab == "entrada" ? true : false
      }

      let resp = await api.post("/api/mvas/index", data);

      this.obs.mvas = [];
      this.obs.length_mvas = 0;

      if (resp.data.itens.length > 0) {
        this.obs.mvas = resp.data.itens
        this.obs.length_mvas = resp.data.count
      }

    } catch (ex) {
      toast.error("Erro ao filtrar Mvas!", {});
    }
  }

  /**
  * Envia um objeto MVA para criação na API administrativa.
  *
  * A função:
  * - Realiza uma requisição POST para o endpoint `/api/admin/mvas` com o objeto `mva` fornecido.
  * - Se a resposta indicar sucesso (`resp.data.ok` for verdadeiro):
  *   - Exibe um **toast.success** com a mensagem "MVA criado".
  *   - Retorna `true`.
  * - Se a resposta não for bem-sucedida ou ocorrer erro na requisição:
  *   - Exibe um **toast.error** com a mensagem "Erro ao criar MVA!".
  *   - Retorna `false`.
  *
  * @async
  * @param {Object} mva Objeto MVA a ser enviado para criação.
  *
  * @returns {Promise<boolean>} Retorna `true` se o MVA for criado com sucesso, caso contrário `false`.
  */

  async doSaveCreateMva(mva) {
    try {
      let resp = await api_adm.post("/api/admin/mvas", mva);

      if (resp.data.ok) {
        toast.success('MVA criado', {});
        return true

      } else {
        toast.error(`Erro ao criar MVA!`, {});
      }

    } catch (ex) {
      toast.error(`Erro ao criar MVA!`, {});
    }

    return false
  }

  /**
  * Exclui um MVA com base no seu ID.
  *
  * A função:
  * - Envia uma requisição DELETE para o endpoint `/api/admin/mvas/{id}`.
  * - Se a resposta indicar sucesso (`resp.data.ok` for verdadeiro):
  *   - Exibe um **toast.success** com a mensagem "MVA excluído".
  *   - Retorna `true`.
  * - Se a exclusão falhar ou a resposta não for positiva:
  *   - Exibe um **toast.error** com a mensagem "Erro ao excluir MVA!".
  *   - Retorna `false`.
  * - Em caso de exceção na requisição, exibe um **toast.error** e também retorna `false`.
  *
  * @async
  * @param {number|string} id Identificador do MVA a ser excluído.
  *
  * @returns {Promise<boolean>} Retorna `true` se o MVA for excluído com sucesso, caso contrário `false`.
  */

  async handleDeleteMva(id) {
    try {
      let resp = await api_adm.delete(`/api/admin/mvas/${id}`);

      if (resp.data.ok) {
        toast.success('MVA excluído', {});
        return true

      } else {
        toast.error(`Erro ao excluir MVA!`, {});
      }

    } catch (ex) {
      toast.error(`Erro ao excluir MVA!`, {});
    }

    return false
  }

  /**
  * Atualiza os dados de um MVA existente na API administrativa.
  *
  * A função:
  * - Envia uma requisição PUT para o endpoint `/api/admin/mvas/{id}`, onde `{id}` é o identificador do MVA (`mva.id`).
  * - O corpo da requisição é o próprio objeto `mva` contendo os dados atualizados.
  * - Se a resposta indicar sucesso (`resp.data.ok` for verdadeiro):
  *   - Exibe um **toast.success** com a mensagem "MVA Editado".
  *   - Retorna `true`.
  * - Se a atualização falhar ou a resposta não for positiva:
  *   - Exibe um **toast.error** com a mensagem "Erro ao salvar MVA!".
  *   - Retorna `false`.
  * - Em caso de erro na requisição (exceção), exibe um **toast.error** e também retorna `false`.
  *
  * @async
  * @param {Object} mva Objeto MVA contendo os dados atualizados, incluindo o campo `id`.
  *
  * @returns {Promise<boolean>} Retorna `true` se o MVA for editado com sucesso, caso contrário `false`.
  */

  async doSaveEditMva(mva) {
    try {
      let resp = await api_adm.put(`/api/admin/mvas/${mva.id}`, mva);

      if (resp.data.ok) {
        toast.success('MVA Editado', {});
        return true

      } else {
        toast.error(`Erro ao salvar MVA!`, {});
      }

    } catch (ex) {
      toast.error(`Erro ao salvar MVA`, {});
    }

    return false
  }

  /**
  * Duplica um MVA existente criando um novo registro com os dados fornecidos.
  *
  * A função:
  * - Envia uma requisição POST para o endpoint `/api/admin/mvas` com o objeto `mva` como corpo.
  * - Presume que o objeto `mva` foi previamente clonado ou modificado para representar um novo MVA com base em um existente.
  * - Se a resposta indicar sucesso (`resp.data.ok` for verdadeiro):
  *   - Exibe um **toast.success** com a mensagem "MVA Duplicado".
  *   - Retorna `true`.
  * - Se a duplicação falhar ou a resposta não for positiva:
  *   - Exibe um **toast.error** com a mensagem "Erro ao duplicar MVA!".
  *   - Retorna `false`.
  * - Em caso de erro na requisição (exceção), exibe um **toast.error** e também retorna `false`.
  *
  * @async
  * @param {Object} mva Objeto MVA que será duplicado.
  *
  * @returns {Promise<boolean>} Retorna `true` se a duplicação for bem-sucedida, caso contrário `false`.
  */

  async doSaveDuplicaMva(mva) {
    try {
      let resp = await api_adm.post('/api/admin/mvas', mva);

      if (resp.data.ok) {
        toast.success('MVA Duplicado', {});
        return true

      } else {
        toast.error(`Erro ao duplicar MVA!`, {});
      }

    } catch (ex) {
      toast.error(`Erro ao duplicar MVA`, {});
    }

    return false
  }

  /**
  * Exporta os dados de MVAs para um arquivo Excel com base no estado e tipo de operação (entrada ou saída).
  *
  * A função:
  * - Envia uma requisição POST para o endpoint `/api/mvas/excel` com os parâmetros:
  *   - `estado`: sigla do estado a ser filtrado.
  *   - `entrada`: booleano indicando se os dados são de entrada (`true`) ou saída (`false`).
  * - Se a resposta for bem-sucedida (`resp.data.ok` for verdadeiro):
  *   - Constrói um link de download com base no conteúdo `base64` retornado (formato `.xlsx`).
  *   - Cria dinamicamente um elemento `<a>` e aciona o clique para iniciar o download do arquivo chamado `export_mvas.xlsx`.
  * - Se a resposta contiver erro (`resp.data.ok` for falso), exibe um **toast.warning** com a mensagem de erro retornada.
  * - Em caso de falha na requisição (exceção), exibe um **toast.warning** com a mensagem "Erro ao exportar, tente novamente mais tarde.".
  *
  * @async
  * @param {string} estado Sigla do estado utilizado como filtro na exportação.
  * @param {boolean} entrada Indica se a exportação deve considerar os dados de entrada (`true`) ou de saída (`false`).
  *
  * @returns {Promise<void>} Promise resolvida após a tentativa de exportação e download do arquivo.
  */

  async handleExport(estado: string, entrada: boolean) {

    try {
      let resp = await api.post("/api/mvas/excel", {estado: estado, entrada: entrada});

      if (resp.data.ok) {
        const linkSource = `data:application/xlsx;base64,${resp.data.base64}`;
        const downloadLink = document.createElement("a");
        const fileName = "export_mvas.xlsx";
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();

      } else {
        toast.warning(resp.data.error, {});
      }

    } catch (ex) {

      toast.warning('Erro ao exportar, tente novamente mais tarde.', {});

    }

  }

  /**
  * Importa um arquivo de MVAs em formato Excel e envia os dados para a API administrativa.
  *
  * A função:
  * - Envia uma requisição POST para o endpoint `/api/admin/mvas/excel/import` com os dados do arquivo (provavelmente um `FormData` com o Excel).
  * - Se a resposta for bem-sucedida (`resp.data.ok` for verdadeiro):
  *   - Exibe um **toast.success** com a mensagem "Arquivo importado com sucesso!".
  *   - Retorna `true`.
  * - Caso contrário:
  *   - Exibe um **toast.error** com a mensagem de erro retornada pela API (`resp.data.error`).
  *   - Retorna `false`.
  *
  * @async
  * @param {any} data Dados do arquivo a ser importado (geralmente um objeto `FormData` contendo o Excel).
  *
  * @returns {Promise<boolean>} Retorna `true` se a importação for bem-sucedida, caso contrário `false`.
  */

  async handleImport(data) {

    let resp = await api_adm.post("/api/admin/mvas/excel/import", data)

    if (resp.data.ok) {

      toast.success('Arquivo importado com sucesso!', {});
      
      return true

    }
        
    toast.error(resp.data.error, {});

    return false
    
  }

}

export const MvaStorePA = new _MvaStorePA();
